import React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchWarehousePurchaseOrdersPage,
  viewerTimeZone,
  warehouseQueryKeys,
  type WarehousePurchaseOrderRow,
  type WarehousePurchaseOrdersParams,
} from '@/data/warehouse';
import { fetchVendorOptions } from '@/data/procurement';
import { ALL_SITE_FILTER, summarizeSiteFilterSelection } from '@/domain/library';
import { formatCount, formatDay, formatMoney, formatRelativeTime, pluralize } from '@/domain/warehouse';
import SearchableMultiSelect, { type MultiSelectOption } from '../../../components/SearchableMultiSelect';
import StageFilterToolbar from '../../../components/events/StageFilterToolbar';
import type { GroupedTableColumn } from '../../../components/common/GroupedDataTable';
import LibraryRecordCard, { LibraryRecordCardDetail } from '../../../components/common/LibraryRecordCard';
import MaintenanceActionsDropdown, { type MaintenanceAction } from '../../../components/admin/MaintenanceActionsDropdown';
import { LateDate, SourceTag, StatusChip } from '../../../components/status/StatusChip';
import LibraryListToolbar, { type LibraryListSortOption } from '../../../components/library/LibraryListToolbar';
import LibraryListBody from '../../../components/library/LibraryListBody';
import WarehouseHero, { type WarehouseHeroStat } from './WarehouseHero';
import WarehouseMeter from './WarehouseMeter';
import { useWarehouseListState, type WarehouseListConfig, type WarehouseViewMode } from './useWarehouseListState';
import { buildStatusFilterModel } from './warehouseStatusFilters';
import { FOLD_TAIL, warehouseCountText } from './warehouseListText';
import { warehouseRecordHref } from './warehouseTabs';
import { WAREHOUSE_PAGE_SIZE, type WarehouseTabContext } from './shared';

const STATUS_MODEL = buildStatusFilterModel('purchaseOrder');
const VIEWS: readonly WarehouseViewMode[] = ['list', 'grid'];
const VIEW_OPTIONS = [
  { value: 'list' as const, label: 'List' },
  { value: 'grid' as const, label: 'Grid' },
];
const SORT_OPTIONS: readonly LibraryListSortOption[] = [
  { value: 'expected', label: 'Expected date' },
  { value: 'ordered', label: 'Order date' },
  { value: 'doc', label: 'PO number' },
  { value: 'status', label: 'Status' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'total', label: 'Total' },
  { value: 'updated', label: 'Last update' },
];
const DESCENDING_FIRST = ['updated', 'total'];
const NOUN = { singular: 'purchase order', plural: 'purchase orders' };
const GROUP_HINTS = { late: 'up to 30 days', drafts: 'not sent yet' };
/** Statuses still waiting on the vendor, so the row menu offers Receive. */
const RECEIVABLE = new Set(['submitted', 'acknowledged', 'partially_received']);

export const PURCHASE_ORDERS_DEFAULT_CONFIG: WarehouseListConfig = {
  search: '',
  sites: [ALL_SITE_FILTER],
  facet: 'all',
  statuses: STATUS_MODEL.defaults,
  chips: [],
  grouping: 'date',
  fold: true,
  sortKey: 'expected',
  sortDir: 'asc',
  view: 'list',
  dateFrom: '',
  dateTo: '',
  secondDateFrom: '',
  secondDateTo: '',
  vendorIds: [],
  orderedBy: '',
};

type PurchaseOrdersTabProps = {
  context: WarehouseTabContext;
  currentProfileId: string | null;
  /** Edit on maintenance.purchase_orders: create and receive (managers only view). */
  canEdit: boolean;
  onNewPurchaseOrder: () => void;
  onScanPackingSlip?: () => void;
};

function receivedCell(row: WarehousePurchaseOrderRow): React.ReactNode {
  if (row.kind === 'labor') {
    return row.laborLinesDone > 0
      ? `${formatCount(row.laborLinesDone)} of ${pluralize(row.laborLineCount, 'labor line')} done`
      : pluralize(row.laborLineCount, 'labor line');
  }
  return <WarehouseMeter done={row.unitsReceived} total={row.unitsOrdered} />;
}

function receivedText(row: WarehousePurchaseOrderRow): string {
  if (row.kind === 'labor') return pluralize(row.laborLineCount, 'labor line');
  return `${formatCount(row.unitsReceived)} of ${formatCount(row.unitsOrdered)} received`;
}

export default function PurchaseOrdersTab({
  context,
  currentProfileId,
  canEdit,
  onNewPurchaseOrder,
  onScanPackingSlip,
}: PurchaseOrdersTabProps) {
  const navigate = useNavigate();
  const state = useWarehouseListState({
    tab: 'purchase_orders',
    profileId: context.profileId,
    prefs: context.prefs,
    prefsLoaded: context.prefsLoaded,
    defaults: PURCHASE_ORDERS_DEFAULT_CONFIG,
    views: VIEWS,
    allStatuses: STATUS_MODEL.statuses,
    siteOptionIds: context.siteOptionIds,
    regions: context.regionFilterOptions,
    scopeKey: context.linkedEvent?.id ?? null,
    descendingFirst: DESCENDING_FIRST,
  });
  const { config, update, page, setPage, sortFor, setGrouping } = state;
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);

  // 648 vendors today: loaded when the vendor filter is first needed, then cached.
  const vendorsQuery = useQuery({
    queryKey: ['warehouse', 'vendor-options'],
    queryFn: fetchVendorOptions,
    enabled: advancedOpen || sheetOpen || config.vendorIds.length > 0,
    staleTime: 5 * 60_000,
  });
  const vendorOptions = React.useMemo<MultiSelectOption[]>(
    () => (vendorsQuery.data ?? []).map((vendor) => ({ id: vendor.vendor_id, label: vendor.name })),
    [vendorsQuery.data],
  );

  const pastExpectedChip = config.chips.includes('past_expected');
  const params = React.useMemo<WarehousePurchaseOrdersParams>(() => ({
    search: state.debouncedSearch,
    locationIds: state.locationIds,
    statuses: state.statusesParam,
    chips: config.chips,
    eventId: context.linkedEvent?.id ?? null,
    includeFold: !config.fold || state.showFolded || pastExpectedChip,
    groupBy: config.grouping,
    sortKey: config.sortKey,
    sortDir: config.sortDir,
    offset: (page - 1) * WAREHOUSE_PAGE_SIZE,
    limit: WAREHOUSE_PAGE_SIZE,
    timezone: viewerTimeZone(),
    kind: config.facet === 'goods' || config.facet === 'labor' ? config.facet : 'all',
    expectedFrom: config.dateFrom || null,
    expectedTo: config.dateTo || null,
    orderedFrom: config.secondDateFrom || null,
    orderedTo: config.secondDateTo || null,
    vendorIds: config.vendorIds,
    orderedBy: config.orderedBy === 'me' ? currentProfileId : null,
  }), [config, context.linkedEvent, currentProfileId, page, pastExpectedChip, state.debouncedSearch, state.locationIds, state.showFolded, state.statusesParam]);

  const pageQuery = useQuery({
    queryKey: warehouseQueryKeys.purchaseOrders(params),
    queryFn: () => fetchWarehousePurchaseOrdersPage(params),
    enabled: !state.noStatuses,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const data = state.noStatuses ? undefined : pageQuery.data;
  const stats = data?.stats ?? {};
  const chipCounts = data?.chipCounts ?? {};
  const total = data?.total ?? 0;
  const loading = !state.noStatuses && pageQuery.isLoading && !data;
  const statValue = (key: string) => (data ? formatCount(stats[key] ?? 0) : '…');

  const heroStats: WarehouseHeroStat[] = [
    {
      key: 'open',
      label: 'Open orders',
      value: statValue('open'),
      hint: data ? `${formatCount(stats.open_goods ?? 0)} goods · ${formatCount(stats.open_labor ?? 0)} subcontracted labor` : null,
    },
    { key: 'waiting', label: 'Waiting on vendor', value: statValue('waiting_on_vendor'), hint: 'Submitted or acknowledged' },
    { key: 'partial', label: 'Partially received', value: statValue('partially_received'), hint: 'Items or work still due' },
    {
      key: 'past_expected',
      label: 'Past expected date',
      value: statValue('past_expected'),
      hint: data ? `${formatCount(stats.open_over_30_days ?? 0)} R2 orders open more than 30 days` : null,
      tone: (stats.past_expected ?? 0) > 0 ? 'warn' : null,
    },
  ];

  const handleGroupingChange = React.useCallback(function handleGroupingChange(event: React.ChangeEvent<HTMLSelectElement>) {
    setGrouping(event.target.value);
  }, [setGrouping]);
  const handleSortChange = React.useCallback(function handleSortChange(sortKey: string, sortDir: 'asc' | 'desc') {
    update({ sortKey, sortDir });
  }, [update]);

  const rowActions = React.useCallback((row: WarehousePurchaseOrderRow): MaintenanceAction[] => {
    const href = warehouseRecordHref.purchaseOrder(row.id);
    const actions: MaintenanceAction[] = [{ key: 'open', label: 'Open purchase order', onSelect: () => navigate(href) }];
    if (canEdit && row.kind === 'goods' && RECEIVABLE.has(row.status)) {
      actions.push({
        key: 'receive',
        label: 'Receive items',
        onSelect: () => navigate(`${warehouseRecordHref.purchaseOrder(row.id, 'receiving')}?receive=1`),
      });
    }
    return actions;
  }, [canEdit, navigate]);
  const renderMenu = (row: WarehousePurchaseOrderRow) => (
    <MaintenanceActionsDropdown menuOnly menuLabel={`Actions for ${row.docNo ?? 'purchase order'}`} actions={rowActions(row)} />
  );

  const columns: GroupedTableColumn<WarehousePurchaseOrderRow>[] = [
    {
      key: 'status',
      header: 'Status',
      width: '9.5rem',
      sort: sortFor('status'),
      render: (row) => <StatusChip kind="purchaseOrder" status={row.status} />,
    },
    {
      key: 'po',
      header: 'Purchase order',
      sort: sortFor('doc'),
      render: (row) => (
        <>
          <span className="library-list-doc">
            <Link className="library-list-doc__link" to={warehouseRecordHref.purchaseOrder(row.id)}>{row.docNo ?? 'Purchase order'}</Link>
            {row.source === 'r2' ? <SourceTag source="r2" /> : null}
            <span className="warehouse-kind-tag">{row.kind === 'labor' ? 'Labor' : 'Goods'}</span>
          </span>
          <span className="grouped-data-table__sub">
            {row.vendor?.name ?? 'No vendor'}
            {row.site?.name ? <span className="grouped-data-table__narrow-only">{` · ${row.site.name}`}</span> : null}
          </span>
        </>
      ),
    },
    {
      key: 'site',
      header: 'Site',
      optional: true,
      render: (row) => row.site?.name ?? <span className="fg-muted">{'—'}</span>,
    },
    {
      key: 'event',
      header: 'Event',
      render: (row) => (row.event ? (
        <>
          <Link className="library-list-doc__link" to={warehouseRecordHref.event(row.event.id)}>{row.event.code ?? 'Event'}</Link>
          {row.event.name ? <span className="grouped-data-table__sub">{row.event.name}</span> : null}
        </>
      ) : <span className="fg-muted">{'—'}</span>),
    },
    {
      key: 'expected',
      header: 'Expected',
      width: '9.5rem',
      sort: sortFor('expected'),
      render: (row) => (
        <>
          <span className="num">{formatDay(row.expectedDay) ?? 'No date'}</span>
          {row.daysLate ? <> <LateDate daysLate={row.daysLate} /></> : null}
          <span className="grouped-data-table__sub">
            {row.status === 'draft' ? 'Not sent yet' : row.orderedDay ? `Ordered ${formatDay(row.orderedDay)}` : null}
          </span>
        </>
      ),
    },
    {
      key: 'received',
      header: 'Received',
      width: '8rem',
      render: (row) => <span className="num">{receivedCell(row)}</span>,
    },
    {
      key: 'total',
      header: 'Total',
      width: '7.5rem',
      align: 'end',
      sort: sortFor('total'),
      render: (row) => <span className="num">{formatMoney(row.totalAmount) ?? '—'}</span>,
    },
    {
      key: 'updated',
      header: 'Updated',
      width: '7rem',
      optional: true,
      sort: sortFor('updated'),
      render: (row) => <span className="fg-muted">{formatRelativeTime(row.lastActivityAt) ?? '—'}</span>,
    },
    {
      key: 'menu',
      header: <span className="sr-only">Actions</span>,
      width: '3rem',
      className: 'grouped-data-table__menu-cell',
      render: renderMenu,
    },
  ];

  const serverFold = data?.fold ?? null;
  const foldCopy = config.fold && !pastExpectedChip && serverFold ? {
    title: 'Open more than 30 days',
    body: `${formatCount(serverFold.count)} R2 purchase orders were ordered more than 30 days ago and are still open. ${FOLD_TAIL}`,
  } : null;

  const openOnly = !state.statusFilterActive;
  const countText = warehouseCountText({ loading, page, pageSize: WAREHOUSE_PAGE_SIZE, total, noun: NOUN, openOnly, fold: serverFold });
  const sortLabel = (SORT_OPTIONS.find((option) => option.value === config.sortKey)?.label ?? config.sortKey).toLowerCase();

  const renderCard = (row: WarehousePurchaseOrderRow) => {
    const href = warehouseRecordHref.purchaseOrder(row.id);
    return (
      <LibraryRecordCard
        key={row.id}
        href={href}
        onOpen={() => navigate(href)}
        code={row.docNo ?? 'Purchase order'}
        badges={(
          <>
            {row.source === 'r2' ? <SourceTag source="r2" className="library-record-card__pill" /> : null}
            <StatusChip kind="purchaseOrder" status={row.status} size="sm" className="library-record-card__pill" />
          </>
        )}
        title={row.vendor?.name ?? 'No vendor'}
        details={(
          <>
            <LibraryRecordCardDetail>{[row.kind === 'labor' ? 'Labor' : 'Goods', row.site?.name].filter(Boolean).join(' · ')}</LibraryRecordCardDetail>
            <LibraryRecordCardDetail muted>
              {row.expectedDay ? `Expected ${formatDay(row.expectedDay)}` : 'No expected date'}
              {row.daysLate ? <> <LateDate daysLate={row.daysLate} /></> : null}
            </LibraryRecordCardDetail>
          </>
        )}
        meta={[receivedText(row), formatMoney(row.totalAmount), row.event?.code ?? null]}
        actions={renderMenu(row)}
      />
    );
  };

  const siteControl = (
    <SearchableMultiSelect
      options={context.siteOptions}
      selected={config.sites}
      onChange={state.setSites}
      placeholder="All sites"
      ariaLabel="Filter by site"
      className="events-site-filter"
      selectionSummary={summarizeSiteFilterSelection(config.sites, context.siteOptions, 'All sites')}
      showPills={false}
    />
  );
  const statusControl = (
    <StageFilterToolbar
      selectedStatuses={state.selectedStatusSet}
      onSelectedStatusesChange={state.setStatuses}
      stageGroups={STATUS_MODEL.stageGroups}
      standaloneStatuses={[]}
      orderStageLookup={STATUS_MODEL.lookup}
      defaultSelectedStatuses={STATUS_MODEL.defaults}
      label="Status"
      pluralNoun="statuses"
      allLabel="All statuses"
      defaultSelectionLabel="Open"
      compact
    />
  );

  return (
    <>
      <div className="settings-page__pinned">
        <WarehouseHero
          isPhone={context.isPhone}
          statsLabel="Purchase order summary"
          stats={heroStats}
          actions={(
            <>
              {canEdit ? <button type="button" className="btn btn-primary" onClick={onNewPurchaseOrder}>New purchase order</button> : null}
              {canEdit && onScanPackingSlip ? <button type="button" className="btn" onClick={onScanPackingSlip}>Scan packing slip</button> : null}
            </>
          )}
        />
        {context.tabStrip}
        <LibraryListToolbar
          search={config.search}
          onSearchChange={(value) => update({ search: value })}
          searchPlaceholder="PO #, vendor, item or event"
          searchPlaceholderShort="PO #, vendor or item"
          searchLabel="Search purchase orders"
          siteControl={siteControl}
          siteFilterActive={state.siteFilterActive}
          facetLabel="Order type"
          facetOptions={[
            { value: 'all', label: 'All' },
            { value: 'goods', label: 'Goods', count: stats.open_goods ?? null },
            { value: 'labor', label: 'Labor', count: stats.open_labor ?? null },
          ]}
          facet={config.facet}
          onFacetChange={(value) => update({ facet: value })}
          statusControl={statusControl}
          statusFilterActive={state.statusFilterActive}
          quickFilters={[
            { key: 'past_expected', label: 'Past expected date', count: chipCounts.past_expected ?? null, active: pastExpectedChip, tone: 'warn' },
            { key: 'linked_to_event', label: 'Linked to an event', count: null, active: config.chips.includes('linked_to_event') },
          ]}
          onToggleQuickFilter={state.toggleChip}
          extraChip={context.linkedEvent ? { label: `Linked to ${context.linkedEvent.code ?? 'event'}`, onClear: context.onClearLinkedEvent } : null}
          countText={countText}
          viewOptions={VIEW_OPTIONS}
          view={config.view}
          onViewChange={(view) => update({ view })}
          viewAriaLabel="Warehouse view"
          advancedOpen={advancedOpen}
          onToggleAdvanced={() => setAdvancedOpen((open) => !open)}
          advancedActiveCount={state.advancedActiveCount}
          advancedFields={(
            <>
              <div className="library-toolbar__field text-xxs fg-muted">
                <span>Expected date</span>
                <div className="library-toolbar__range">
                  <input type="date" aria-label="Expected from" value={config.dateFrom} onChange={(event) => update({ dateFrom: event.target.value })} />
                  <span aria-hidden="true">to</span>
                  <input type="date" aria-label="Expected to" value={config.dateTo} onChange={(event) => update({ dateTo: event.target.value })} />
                </div>
              </div>
              <div className="library-toolbar__field text-xxs fg-muted">
                <span>Ordered date</span>
                <div className="library-toolbar__range">
                  <input type="date" aria-label="Ordered from" value={config.secondDateFrom} onChange={(event) => update({ secondDateFrom: event.target.value })} />
                  <span aria-hidden="true">to</span>
                  <input type="date" aria-label="Ordered to" value={config.secondDateTo} onChange={(event) => update({ secondDateTo: event.target.value })} />
                </div>
              </div>
              <div className="library-toolbar__field text-xxs fg-muted">
                <span>Vendor</span>
                <SearchableMultiSelect
                  options={vendorOptions}
                  selected={config.vendorIds}
                  onChange={state.setVendorIds}
                  placeholder={vendorsQuery.isLoading ? 'Loading vendors…' : 'All vendors'}
                  ariaLabel="Filter by vendor"
                  showPills={false}
                />
              </div>
              <label className="library-toolbar__field text-xxs fg-muted">
                <span>Ordered by</span>
                <select className="xs" value={config.orderedBy === 'me' ? 'me' : ''} onChange={(event) => update({ orderedBy: event.target.value })}>
                  <option value="">Anyone</option>
                  <option value="me">Me</option>
                </select>
              </label>
              <label className="library-toolbar__field text-xxs fg-muted">
                <span>Grouping</span>
                <select className="xs" value={config.grouping} onChange={handleGroupingChange} aria-label="Group rows by">
                  <option value="off">Off</option>
                  <option value="date">Expected date</option>
                  <option value="status">Status</option>
                  <option value="vendor">Vendor</option>
                  <option value="site">Site</option>
                </select>
              </label>
              <div className="library-toolbar__field text-xxs fg-muted warehouse-fold-toggle">
                <span>Stale R2 orders</span>
                <label className="warehouse-fold-toggle__control">
                  <input type="checkbox" checked={config.fold} onChange={(event) => update({ fold: event.target.checked })} />
                  Fold R2 orders open more than 30 days
                </label>
              </div>
            </>
          )}
          sortOptions={SORT_OPTIONS}
          sortKey={config.sortKey}
          sortDir={config.sortDir}
          onSortChange={handleSortChange}
          savedViews={state.savedViews}
          onResetAdvanced={state.resetAdvanced}
          onResetAll={state.resetAll}
          compact={context.compactToolbar}
          sheetResultLabel={loading ? 'Show purchase orders' : `Show ${formatCount(total)} ${total === 1 ? 'purchase order' : 'purchase orders'}`}
          onSheetOpenChange={setSheetOpen}
        />
      </div>

      <main className="settings-content warehouse-content">
        <LibraryListBody
          panelId={context.panelId}
          labelledBy={context.tabElementId('purchase_orders')}
          noun={NOUN}
          view={config.view}
          isPhone={context.isPhone}
          data={data}
          loading={loading}
          error={state.noStatuses ? null : pageQuery.error}
          onRetry={() => { void pageQuery.refetch(); }}
          blockedMessage={state.noStatuses ? 'Pick at least one status to see purchase orders.' : null}
          columns={columns}
          grouped={config.grouping !== 'off'}
          groupHints={GROUP_HINTS}
          renderCard={renderCard}
          fold={foldCopy}
          onToggleFold={state.toggleFolded}
          emptyTitle="No purchase orders match these filters"
          emptyHint={`Adjust the search or filters${canEdit ? ', or start a new purchase order' : ''}.`}
          page={page}
          pageSize={WAREHOUSE_PAGE_SIZE}
          onPageChange={setPage}
          pageNote={config.grouping === 'off' ? `sorted by ${sortLabel}` : 'searched and sorted on the server, so every order is reachable'}
        />
      </main>
    </>
  );
}
